import React, { useEffect, useMemo, useRef, useState } from "react";
import ViewMoreBtn from "../../components/ViewMoreBtn";
import DataTable from "react-data-table-component";
import { LuFilter } from "react-icons/lu";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { AiOutlineDelete } from "react-icons/ai";
import { BsBellFill } from "react-icons/bs";
import io from "socket.io-client";
import DeleteModal from "../../components/DeleteModal";

const Chats = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { admin } = useSelector((state) => state.userSlice);
  const { refetch } = useSelector((state) => state.chatSlice);
  const socketRef = useRef();

  const prevProps = useRef({ refetch }).current;

  useEffect(() => {
    if (prevProps.refetch !== refetch && socketRef.current?.connected) {
      loadChats();
    }
    return () => {
      prevProps.refetch = refetch;
    };
  }, [refetch]);

  // connect with the sockets
  useEffect(() => {
    if (Object.keys(admin).length === 0) return;
    
    console.log("Initializing Socket.IO connection...");
    
    // Initialize Socket.IO connection
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      extraHeaders: {
        token: localStorage.getItem("sgtoken"),
      },
    });

    // Set loading only on first load
    const isFirstLoad = data.length === 0;
    if (isFirstLoad) {
      setIsLoading(true);
    }

    // Add event listeners
    const handleConnect = () => {
      console.log("Connected Successfully");
      socket.emit("adduser", { id: admin._id });
      socket.emit("joinchat", { id: admin._id });
      loadChats(socket);
    };

    const handleNewChat = () => {
      console.log("Received new chat");
      loadChats(socket);
    };

    socket.on("connect", handleConnect);
    socket.on("receivechat", handleNewChat);

    socketRef.current = socket;

    // Cleanup function
    return () => {
      console.log("Disconnecting now");
      socket.off("connect", handleConnect);
      socket.off("receivechat", handleNewChat);
      socket.disconnect();
    };
  }, [admin]);

  const loadChats = (socket = socketRef.current) => {
    if (!socket || !socket.connected) {
      console.log("Socket not connected, cannot load chats");
      setIsLoading(false);
      return;
    }

    // Only show loading if it's the first load
    const isFirstLoad = data.length === 0;
    if (isFirstLoad) {
      setIsLoading(true);
    }

    socket.emit("getChatList", { id: admin._id }, (response) => {
      console.log("Chat list response:", response);
      if (response && response.data) {
        setData(Array.isArray(response.data) ? response.data : []);
      } else {
        setData([]);
      }
      setIsLoading(false);
    });
  };

  const columns = [
    {
      name: "Messaged At",
      selector: (row) => {
        const date = new Date(row.createdAt);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        });
        const formattedTime = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${formattedDate.replace(/\//g, "-")} at ${formattedTime}`;
      },
      sortable: true,
    },
    {
      name: "Sender",
      selector: (row) => {
        return (
          <div className="d-flex align-items-center">
            <span>{row?.sender?.firstName + " " + row?.sender?.lastName}</span>
            {row.unreadCount > 0 && (
              <div className="unread-badge ms-2">{row.unreadCount}</div>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Message",
      selector: (row) => row?.latestMessage?.message,
      sortable: true,
      wrap: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="d-flex">
          <AiOutlineDelete
            style={{ minWidth: "max-content" }}
            onClick={() => {
              setDeleteId(row?._id);
              setDeleteModal(true);
            }}
            size={19}
          />
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <ViewMoreBtn redirectUrl={`/chat/${row._id}`} />
        </div>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const fullName = `${item?.sender?.firstName || ""} ${
        item?.sender?.lastName || ""
      }`.toLowerCase();

      return (
        fullName.includes(searchText.toLowerCase()) &&
        (category ? item.status?.props?.children === category : true)
      );
    });
  }, [data, searchText, category]);

  const handleSearch = (value) => {
    setSearchText(value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  return (
    <>
      <style>
        {`
          .unread-badge {
            background-color: rgb(8, 91, 40);
            color: white;
            border-radius: 50%;
            min-width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            padding: 0 4px;
          }
          
          .notification-icon {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          
          .notification-count {
            position: absolute;
            top: -8px;
            right: -8px;
            background-color: rgb(8, 91, 40);
            color: white;
            border-radius: 50%;
            min-width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
          }
          
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 300px;
            width: 100%;
          }
        `}
      </style>
      
      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div>
          <div className="page-header">
            <div className="d-flex align-items-center justify-content-between">
              <h3 className="page-title">Chats</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body py-4">
                  <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[
                      10, 20, 30, 50, 100, 200, 500,
                    ]}
                    highlightOnHover
                    pointerOnHover
                    subHeader
                    subHeaderAlign="left"
                    subHeaderComponent={
                      <Row className="mb-3 w-100">
                        <Col md={8} className="d-flex align-items-start flex-column gap-2">
                          <span className="d-flex align-items-center gap-2 me-3 filterby_label">
                            <LuFilter />
                            Filter By :
                          </span>
                          <div className="table_filter">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by name"
                              value={searchText}
                              onChange={(e) => handleSearch(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <DeleteModal
        forChat
        deleteId={deleteId}
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />
    </>
  );
};

export default Chats;
